<template>
	<div v-if="info.length > 0" class="swiper-container">
		<van-swipe
			class="swiper"
			:autoplay="autoplayInterval"
			:duration="duration"
			:show-indicators="indicatorDots"
			indicator-color="#ffffff"
			indicator-color-active="#687df7"
		>
			<van-swipe-item v-for="(item, index) in info" :key="index">
				<a class="swiper-link" :href="getBannerHref(item)" @click.prevent="bannerTo(item)">
					<div class="swiper-item">
						<img style="object-fit: fill; height: 100%; width: 100%" :src="item.mapUrl" :alt="getBannerAlt(item)" />
					</div>
				</a>
			</van-swipe-item>
		</van-swipe>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { getIndexImgList } from '@/api/recommendBrand.js';
import { jumpTo, mateUrl } from '@/utils/mateUrl.js';
import { useAuthStore } from '@/stores/auth';

const emit = defineEmits(['click']);
const authStore = useAuthStore();

// 响应式数据
const info = ref([]);
const indicatorDots = ref(true);
const enableAutoplay = ref(true);
const interval = ref(2000);
const duration = ref(500);
// Vant 的 autoplay 需要数字类型（毫秒），0 表示禁用
const autoplayInterval = computed(() => {
	return enableAutoplay.value ? interval.value : 0;
});

// 处理日期字符串以适配 iOS Safari
const parseDateForIOS = dateStr => {
	if (!dateStr) return NaN;
	const parts = dateStr.split(/[- :]/);
	// 注意月份需要减1（Month 是从 0 开始计数）
	return new Date(parts[0], parts[1] - 1, parts[2], parts[3] || 0, parts[4] || 0, parts[5] || 0).getTime();
};

/**
 * 生成首页主 Banner 的 href，兼容预览图跳转。
 * @param {Object} item Banner 数据
 * @return {string} Banner 锚点输出地址
 */
const getBannerHref = item => {
	if (item?.jumpType == 1) return '/bannerImagePreview';
	const bannerLink = item?.mobileLinkUrl || '';
	return mateUrl(bannerLink) || bannerLink || 'javascript:void(0)';
};

/**
 * 生成首页主 Banner 图片的 alt 文案。
 * @param {Object} item Banner 数据
 * @return {string} 图片替代文本
 */
const getBannerAlt = item => item?.title || item?.columnTitleName || '首页专题';

// Banner 点击处理
const bannerTo = item => {
	emit('click', { attrCode: `banner-${item?.title || ''}` });

	if (item.isLogIn == 1 && !authStore.isLoggedIn) {
		// 跳转到登录页面
		navigateTo('/login', { replace: true });
		return;
	}

	if (item.jumpType == 1) {
		if (!item.imgUrl) return;
		window.sessionStorage.setItem('banner-image-preview', item.imgUrl);
		navigateTo('/bannerImagePreview');
		return;
	}
	if (item.mobileLinkUrl) {
		jumpTo(item.mobileLinkUrl);
	}
};

// 数据过滤逻辑
const filterBannerData = res => {
	if (!res?.data) {
		return [];
	}
	const now = new Date().getTime();
	const excludeUrl = 'https://gycr.topsoft.com.cn/#/dcsso?userName=';
	const { ML } = res.data || {};
	return (
		ML &&
		ML.filter(item => {
			const invalidTime = parseDateForIOS(item.invalidTime);
			const validTime = parseDateForIOS(item.validTime);
			return invalidTime > now && validTime < now && item.linkUrl != excludeUrl;
		})
	);
};

// 服务端渲染获取数据
const { data: bannerData } = await useAsyncData('index-banner', async () => {
	try {
		const res = await getIndexImgList();
		return filterBannerData(res);
	} catch (e) {
		console.error('获取Banner数据失败:', e);
		return [];
	}
});

// 赋值给响应式数据
info.value = bannerData.value || [];
</script>

<style lang="scss" scoped>
.swiper-container {
	width: 100%;
}

.swiper {
	height: 120.67px;
	border-radius: 5px;
	overflow: hidden;
}

.swiper-item {
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.swiper-link {
	display: block;
	height: 100%;
}

:deep(.van-swipe__indicator) {
	width: 5px;
	height: 5px;
	background: #ffffff;
	margin-right: 4px !important;
}

:deep(.van-swipe__indicator--active) {
	width: 10px;
	background: #687df7 !important;
	border-radius: 20px;
}
</style>
